import StorageClient from "../../clients/storageClient";
import { camelCase, identity } from "../../common/util";
import { WORKSPACE } from "../../common/constants";
import { FinsembleWindowData } from "../window/types";
import { LoggerClient as Logger } from "../../clients/logger";
import { StandardErrorCallback } from "../../types";
import pLimit from "p-limit";

const limit = pLimit(1);

const { ACTIVE_WORKSPACE, CACHE_STORAGE_TOPIC, STORAGE_TOPIC } = WORKSPACE;

export const GET_WINDOW_STATE_ERROR = (win: string, ws: string = ACTIVE_WORKSPACE) =>
	`No Window State found for window "${win}" in workspace "${ws}"`;

export type StateType = "windowData" | "componentState";

/**
 * Represents both `windowData` (the window's size, position, url, etc.),
 * and `componentState` (logical data representing linking, custom
 * integerations, etc.)
 */
export type CompleteWindowState = {
	windowData: FinsembleWindowData;
	componentState: Record<string, any>;
};

/**
 * Rate limited functions
 */
const remove1 = (params: { key: string; topic: string }) => limit(() => StorageClient.remove(params));

const get1 = <T = any>(params: { key: string; topic: string }, cb?: StandardErrorCallback<T>): Promise<T> =>
	limit(() => StorageClient.get(params, cb));

const save1 = (params: { key: string; topic: string; value: any }) => limit(() => StorageClient.save(params));

/**
 * Static, ORM-like layer into the storage of all `windowData`
 * and `componentState` data, mediating interface with storage
 * while enforcing consistency and business rules.
 */
export class WindowStorageManager {
	/**
	 * This static member is necessary for testing purposes
	 * only. Ideally, we would just use the StorageClient
	 * instance directly; however, this causes inconsistencies
	 * with our mocks that failed our testing efforts.
	 */
	static _SC = StorageClient;

	static getWindowID = (win: FinsembleWindowData) => camelCase(win.name);

	/**
	 * Asynchronously updates provided key in storage by first retrieving the key
	 * then running a provided function on the result and re-saving its value.
	 * There’s no guarantees of consistency or atomicity
	 *
	 * @param params {any} Update storage params
	 * @param params.topic {string} The storage topic
	 * @param params.key {string} The storage key
	 * @param params.updateFn {Function} Function to run to determine the value to store
	 * @private
	 */
	static async updateStorage(params: { topic: string; key: string; updateFn: (x: any) => any }) {
		const { topic, key, updateFn } = params;
		const result = await WindowStorageManager._SC.get({ topic, key });
		return await WindowStorageManager._SC.save({ topic, key, value: updateFn(result) });
	}

	/**
	 * Rate limited updateStorage.
	 * @private
	 */
	static updateStorage1(params: { topic: string; key: string; updateFn: (x: any) => any }) {
		return limit(() => WindowStorageManager.updateStorage(params));
	}

	/**
	 * Given a type, window, and workspace name, returns the correct storage topic and key
	 * for the `windowData` or `componentState`.
	 * Omit the workspace name to target the active workspace.
	 */
	static getTopicAndKey(type: StateType, windowName: string, workspaceName: string = ACTIVE_WORKSPACE) {
		/** The `windowData` is stored under the key with the format "workspacenameWindowname",
		 * while the `componentState` is stored under "workspacenameWindownameWindowname" */
		const keyArgs = type === "windowData" ? [workspaceName, windowName] : [workspaceName, windowName, windowName];

		return {
			topic: workspaceName === ACTIVE_WORKSPACE ? CACHE_STORAGE_TOPIC : STORAGE_TOPIC,
			key: camelCase.apply(null, keyArgs),
		};
	}

	/**
	 * Retrieves a window or component state belonging to the given workspace from storage.
	 * Omit the workspace name to target the active workspace.
	 *
	 * @param type Either `componentState` or `windowData`.
	 */
	static async getState(type: StateType, windowName: string, workspaceName?: string): Promise<FinsembleWindowData> {
		const topicAndKey = WindowStorageManager.getTopicAndKey(type, windowName, workspaceName);
		//disabled to reduce load on logging system generated when when retrieving activeWorkspace
		//Logger.system.debug("getState", type, windowName, workspaceName, topicAndKey, "Result=", result);

		return ((await get1(topicAndKey)) as FinsembleWindowData) || null;
	}

	/**
	 * Same as `getState` but retrieves both `componentState` and `windowData`.
	 * NOTE: either or both of windowData may be null or emtpy objects (i.e, when
	 * the requested data doesn't exist).
	 */
	static async getCompleteState(windowName: string, workspaceName?: string): Promise<CompleteWindowState> {
		let componentState = (await WindowStorageManager.getState("componentState", windowName, workspaceName)
			// Windows may not necessarily have component state, so we must catch that occurance here.
			.catch(() => {
				return {};
			})) as Record<string, any>;
		let windowData = (await WindowStorageManager.getState(
			"windowData",
			windowName,
			workspaceName
		)) as FinsembleWindowData;
		Logger.system.debug("getCompleteState", windowName, workspaceName, componentState, windowData);

		return { componentState, windowData };
	}

	/**
	 * Retrieves multiple states (`componentState` or `windowData`) belonging to the given workspace from storage.
	 * Omit the workspace name to target the active workspace.
	 *
	 * It's possible to request a window state before it's been saved to storage particularly.
	 * if the window was added to a workspace by name (not by data). Therefore, unlike `getState`,
	 * this method doesn't throw errors if unable to fulfill the request; rather, it logs a warning instead.
	 *
	 * @param type Either `componentState` or `windowData`.
	 */
	static async getManyStates(
		type: StateType,
		windowNames: string[],
		workspaceName?: string
	): Promise<FinsembleWindowData[]> {
		const promises = windowNames.flatMap((x) => {
			try {
				const state = WindowStorageManager.getState(type, x, workspaceName);
				return [state];
			} catch (e) {
				return [];
			}
		});

		return Promise.all(promises).then((x) => x.filter(identity));
	}

	/**
	 * Same as `getManyStates` but retrieves both `componentState` and `windowData`.
	 */
	static async getManyCompleteStates(windowNames: string[], workspaceName?: string): Promise<CompleteWindowState[]> {
		return Promise.all(
			windowNames.map((x) =>
				WindowStorageManager.getCompleteState(x, workspaceName).catch((err) => {
					Logger.system.warn(err);
					return null;
				})
			)
		).then((x) => x.filter(identity)) as unknown as Promise<CompleteWindowState[]>;
	}

	/**
	 * Persists a state to storage under the given workspace name.
	 * Omit the workspace name to target the active workspace.
	 *
	 * @param type Either `componentState` or `windowData`.
	 */
	static async setState(
		type: StateType,
		windowName: string,
		data: FinsembleWindowData | Record<string, any>,
		workspaceName?: string
	): Promise<void> {
		await save1({
			...WindowStorageManager.getTopicAndKey(type, windowName, workspaceName),
			value: data,
		});
	}

	/**
	 * Same as `setState`, but sets both `componentState` and `windowData`.
	 */
	static async setCompleteState(state: CompleteWindowState, workspaceName?: string): Promise<void> {
		// This only happens if a very seldomly hit race condition happens, and a window is removed from storage but not from the active workspace.
		// We didn't spend a ton of time trying to figure it out because the underlying issue had no
		// observable effect.
		// Removing this line will cause the workspace to crash if it hits, though. so leave it.
		if (!state.windowData || !state.componentState) return;
		await WindowStorageManager.setState("windowData", state.windowData.name, state.windowData, workspaceName);
		await WindowStorageManager.setState("componentState", state.windowData.name, state.componentState, workspaceName);
	}

	/**
	 * Given an array of states, persists their states to storage.
	 * Omit the workspace name to target the active workspace.
	 *
	 * @param type Either `componentState` or `windowData`.
	 */
	static async setManyStates(type: StateType, windows: FinsembleWindowData[], workspaceName?: string): Promise<void[]> {
		return Promise.all(windows.map((x) => WindowStorageManager.setState(type, x.name, x, workspaceName)));
	}

	/**
	 * Same as `setManyStates`, but sets both `componentState` and `windowData`.
	 */
	static async setManyCompleteStates(windows: CompleteWindowState[], workspaceName?: string): Promise<void[]> {
		return Promise.all(windows.map((x) => WindowStorageManager.setCompleteState(x, workspaceName)));
	}

	/**
	 * Given a window name and an update function, retrieves the state
	 * under that name from storage, applies the update function to it, then sets
	 * the storage with the result.
	 * Omit the workspace name to target the active workspace.
	 *
	 * @param type Either `componentState` or `windowData`.
	 */
	static async updateState(
		type: StateType,
		updateFn: (window: FinsembleWindowData) => FinsembleWindowData,
		windowName: string,
		workspaceName?
	): Promise<void> {
		await WindowStorageManager.updateStorage1({
			...WindowStorageManager.getTopicAndKey(type, windowName, workspaceName),
			updateFn,
		});
	}

	/**
	 * Removes a window's state (both `componentState` and `windowData`) from storage under the
	 * given workspace name. Omit the workspace name to target the active workspace.
	 *
	 * Logs a warning if unable to fulfill the request (doesn't throw).
	 */
	static async removeCompleteState(windowName: string, workspaceName?: string): Promise<void> {
		try {
			await remove1(WindowStorageManager.getTopicAndKey("windowData", windowName, workspaceName));
			await remove1(WindowStorageManager.getTopicAndKey("componentState", windowName, workspaceName));
		} catch (error) {
			Logger.system.warn(error);
		}
	}

	/**
	 * Removes multiple states (both `componentState` and `windowData`) belonging to the given workspace from storage.
	 * Omit the workspace name to target the active workspace.
	 */
	static async removeManyCompleteStates(windowNames: string[], workspaceName?: string): Promise<void[]> {
		return Promise.all(windowNames.map((x) => WindowStorageManager.removeCompleteState(x, workspaceName)));
	}
}
